import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, PauseCircle, ChevronRight, Lock } from 'lucide-react';
import { useLanguage } from '../LanguageContext'; 
import { t } from '../translations'; 
import { supabase } from '../supabaseClient';

const TopicsView = ({
  fromDate, setFromDate, toDate, setToDate,
  authorQuery, setAuthorQuery, selectedAuthor, setSelectedAuthor, searchForAuthor, isSearchingAuthors, authorSuggestions, selectAuthor,
  journalQuery, setJournalQuery, selectedJournal, setSelectedJournal, searchForJournal, isSearchingJournals, journalSuggestions, selectJournal,
  institutionQuery, setInstitutionQuery, selectedInstitution, setSelectedInstitution, searchForInstitution, isSearchingInstitutions, institutionSuggestions, selectInstitution,
  departmentQuery, setDepartmentQuery,
  selectedTopics, toggleTopic, customTopic, setCustomTopic, addCustomTopic,
  selectedCountries, setSelectedCountries, 
  performGeneration, saveWatch,
  selectedLevel, setSelectedLevel,
  isTrialPaused = false,
  onOpenPaywall
}) => {
  const { language } = useLanguage(); 
  const isEn = language === 'English' || language === 'en';
  const searchInputRef = useRef(null);

  const [isRendered, setIsRendered] = useState(false); 
  const [isVisible, setIsVisible] = useState(false);   
  const [dragY, setDragY] = useState(0);               
  const [touchStartY, setTouchStartY] = useState(null);

  const [aiIdea, setAiIdea] = useState('');
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);

  useEffect(() => {
    if (isRendered && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isRendered]);

  const openFilter = () => {
    setIsRendered(true); 
    setTimeout(() => setIsVisible(true), 10); 
  };

  const closeFilter = () => {
    setIsVisible(false); 
    setDragY(0);
    setTimeout(() => setIsRendered(false), 300); 
  };

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (touchStartY === null) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY;
    
    if (diff > 0) {
      setDragY(diff);
      if (e.cancelable) e.preventDefault(); 
    }
  };

  const handleTouchEnd = () => {
    if (touchStartY === null) return;
    if (dragY > 50) {
      closeFilter();
    } else {
      setDragY(0);
    }
    setTouchStartY(null); 
  };

  const generateKeywordsWithAI = async () => {
    if (!aiIdea.trim()) return;
    
    setIsGeneratingKeywords(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch('/api/suggest-keywords', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
        },
        body: JSON.stringify({ userText: aiIdea })
      });

      if (!response.ok) throw new Error('Kunde inte nå AI:n');
      
      const data = await response.json();
      
      if (data.keywords && data.keywords.length > 0) {
        const searchString = data.keywords.join(' AND ');
        setCustomTopic(searchString);
        
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.classList.add('ring-4', 'ring-indigo-300', 'bg-indigo-50');
          setTimeout(() => {
            searchInputRef.current.classList.remove('ring-4', 'ring-indigo-300', 'bg-indigo-50');
          }, 1500);
        }
      }
    } catch (error) {
      console.error("AI Error:", error);
      alert(t(language, 'topics_ai_error'));
    } finally {
      setIsGeneratingKeywords(false);
      setAiIdea(''); 
    }
  };

  const suggestedTopics = [
    { name: 'Artificial Intelligence', labelKey: 'topic_ai', query: 'Artificial Intelligence', icon: '🤖' },
    { name: 'Climate Change', labelKey: 'topic_climate', query: 'Climate Change', icon: '🌍' },
    { name: 'Oncology', labelKey: 'topic_oncology', query: 'Oncology', icon: '🔬' },
    { name: 'Pedagogy', labelKey: 'topic_pedagogy', query: 'Pedagogy', icon: '📚' },
    { name: 'Quantum Physics', labelKey: 'topic_quantum', query: 'Quantum Physics', icon: '⚛️' }
  ];

  const countryNames = {
    SE: t(language, 'country_se'), NO: t(language, 'country_no'), DK: t(language, 'country_dk'), FI: t(language, 'country_fi'),
    GB: t(language, 'country_gb'), DE: t(language, 'country_de'), FR: t(language, 'country_fr'), NL: t(language, 'country_nl'),
    BE: t(language, 'country_be'), CH: t(language, 'country_ch'), AT: t(language, 'country_at'), IE: t(language, 'country_ie'),
    ES: t(language, 'country_es'), IT: t(language, 'country_it'), PT: t(language, 'country_pt'),
    US: t(language, 'country_us'), CA: t(language, 'country_ca'), IN: t(language, 'country_in'), CN: t(language, 'country_cn'), JP: t(language, 'country_jp')
  };

  useEffect(() => {
    if (window.innerWidth < 768 && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Text-fallbacks som garanterar ren layout utan råa nycklar
  const pausedTitle = t(language, 'inbox_paused_title') !== 'inbox_paused_title'
    ? t(language, 'inbox_paused_title')
    : (isEn ? 'Your watches are paused (20/20 summaries)' : 'Dina bevakningar är pausade (20/20 sammanfattningar)');

  const pausedDesc = t(language, 'topics_paused_desc') !== 'topics_paused_desc'
    ? t(language, 'topics_paused_desc')
    : (isEn ? 'You have reached the trial limit. Upgrade to Basic to continue creating new watches and running test searches.' : 'Du har nått gränsen för provperioden. Uppgradera till Basic för att fortsätta skapa nya bevakningar och köra provsökningar.');

  const pausedBtnText = t(language, 'inbox_paused_btn') !== 'inbox_paused_btn'
    ? t(language, 'inbox_paused_btn')
    : (isEn ? 'Reactivate with Basic (25 SEK/mo)' : 'Återaktivera med Basic (25 kr/mån)');

  return (
    <div className="w-full max-w-3xl mx-auto pb-40 md:pb-10 relative px-4 md:px-0" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
      
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          
          @media (max-width: 767px) {
            .mobile-sheet-transition {
              transform: translateY(var(--sheet-translate, 100%));
              transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            }
            .mobile-sheet-dragging {
              transition: none !important; 
            }
          }
        `}
      </style>

      {/* --- PAUSAD BANNER VID PROVTAK (20/20) --- */}
      {isTrialPaused && (
        <div className="mb-6 p-4 md:p-5 bg-amber-50/90 border border-amber-200/80 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm animate-fade-in">
          <div className="flex items-start gap-3">
            <PauseCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-amber-950">
                {pausedTitle}
              </h4>
              <p className="text-xs text-amber-800/90 mt-0.5 leading-relaxed">
                {pausedDesc}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenPaywall}
            className="w-full sm:w-auto shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 active:scale-95"
          >
            <span>{pausedBtnText}</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* --- HEADER --- */}
      <div className="mb-6 mt-2 md:mt-0 text-center md:text-left">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1 md:mb-2 tracking-tight">{t(language, 'topics_title')}</h1>
        <p className="text-slate-500 text-sm md:text-lg">{t(language, 'topics_subtitle')}</p>
      </div>

      {/* --- AI ASSISTANS --- */}
      <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-3xl shadow-sm mb-6 w-full relative">
        <label className="flex items-center gap-2 text-sm font-bold text-indigo-700 mb-2 uppercase tracking-wider">
          <Sparkles size={16} /> 
          {t(language, 'topics_ai_title')}
        </label>
        <p className="text-xs md:text-sm text-indigo-600/80 mb-3">
          {t(language, 'topics_ai_desc')}
        </p>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={aiIdea}
            onChange={(e) => setAiIdea(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && generateKeywordsWithAI()}
            placeholder={t(language, 'topics_ai_placeholder')}
            className="flex-1 px-4 py-3 md:py-4 bg-white border border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-slate-700 text-sm md:text-base shadow-inner"
            disabled={isGeneratingKeywords}
          />
          <button
            onClick={generateKeywordsWithAI}
            disabled={isGeneratingKeywords || !aiIdea.trim()}
            className={`px-6 py-3 md:py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 active:scale-95 whitespace-nowrap ${
              isGeneratingKeywords || !aiIdea.trim() 
                ? 'bg-indigo-300 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700 shadow-md'
            }`}
          >
            {isGeneratingKeywords ? (
              <><Loader2 size={18} className="animate-spin" /> {t(language, 'topics_ai_thinking')}</>
            ) : (
              <><Sparkles size={18} /> {t(language, 'topics_ai_suggest')}</>
            )}
          </button>
        </div>
      </div>

      {/* --- HUVUDSÖK --- */}
      <div className="bg-white p-5 md:p-8 rounded-3xl shadow-sm border border-slate-200 mb-6 w-full relative box-border" style={{ maxWidth: '100%' }}>
        <label className="block text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">{t(language, 'topics_search_label')}</label>
        
        <div className="flex relative items-center w-full box-border">
          <span className="absolute left-3 md:left-4 text-lg md:text-xl text-slate-400 z-10">🔍</span>
          <input
            ref={searchInputRef}
            type="text"
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomTopic()}
            placeholder={t(language, 'topics_search_placeholder')}
            className="w-full pl-10 md:pl-12 pr-28 md:pr-32 py-3.5 md:py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-slate-800 text-base md:text-lg truncate shadow-inner box-border duration-300"
          />
          <button
            onClick={addCustomTopic}
            className="absolute right-1.5 md:right-2 top-1.5 md:top-2 bottom-1.5 md:bottom-2 bg-slate-800 text-white px-4 md:px-6 rounded-xl font-medium hover:bg-slate-700 transition-colors text-sm md:text-base flex items-center justify-center shadow-sm active:scale-95"
          >
            {t(language, 'topics_add_btn')}
          </button>
        </div>

        {/* --- SNABBA VAL --- */}
        <div className="mt-6 w-full overflow-hidden" style={{ maxWidth: '100%' }}>
          <p className="text-xs text-slate-400 font-bold mb-3 uppercase tracking-wider">{t(language, 'topics_quick_picks')}</p>
          <div className="flex overflow-x-auto hide-scrollbar pb-2 md:flex-wrap gap-2 snap-x snap-mandatory w-full box-border">
            {suggestedTopics.map(topic => {
              const isSelected = selectedTopics.some(t => t.name === topic.name);
              return (
                <button
                  key={topic.name}
                  onClick={() => toggleTopic(topic)}
                  className={`snap-start whitespace-nowrap flex-none px-4 py-2.5 rounded-full text-sm font-semibold transition-all border active:scale-95 ${
                    isSelected 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                      : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <span className="mr-1">{topic.icon}</span> {t(language, topic.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {selectedTopics.filter(t => !suggestedTopics.some(st => st.name === t.name)).length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2 w-full">
            {selectedTopics.filter(t => !suggestedTopics.some(st => st.name === t.name)).map(topic => (
              <span key={topic.name} className="px-4 py-2 bg-slate-800 text-white rounded-full text-sm font-medium flex items-center shadow-sm">
                {topic.name}
                <button onClick={() => toggleTopic(topic)} className="ml-2 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/40 transition-colors active:scale-95 flex-none">✕</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* --- AI SVÅRIGHETSGRAD --- */}
      <div className="bg-white p-5 md:p-8 rounded-3xl shadow-sm border border-slate-200 mb-6 w-full relative box-border" style={{ maxWidth: '100%' }}>
        <label className="block text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">
          {t(language, 'topics_difficulty_label') || 'Nivå på AI-sammanfattning'}
        </label>
        
        <div className="relative w-full box-border">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl z-10 pointer-events-none">🧠</span>
          <select
            value={selectedLevel || 'allmänhet'}
            onChange={(e) => setSelectedLevel && setSelectedLevel(e.target.value)}
            className="w-full pl-12 pr-10 py-3.5 md:py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-slate-800 text-base md:text-lg appearance-none cursor-pointer box-border shadow-inner duration-300"
          >
            <option value="allmänhet">{t(language, 'topics_diff_public')}</option>
            <option value="student">{t(language, 'topics_diff_student')}</option>
            <option value="expert">{t(language, 'topics_diff_expert')}</option>
          </select>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
            ▼
          </div>
        </div>
      </div>

      {/* --- FILTER-TRIGGER --- */}
      <button 
        onClick={openFilter}
        className="w-full flex items-center justify-between p-5 bg-white rounded-2xl shadow-sm border border-slate-200 mb-6 hover:bg-slate-50 transition-colors active:scale-95 box-border"
      >
        <span className="font-bold text-slate-700 flex items-center">
          <svg className="w-5 h-5 mr-3 text-slate-500 flex-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path></svg>
          {t(language, 'topics_filter_title')}
        </span>
        <span className="text-blue-600 font-semibold text-sm bg-blue-50 px-3 py-1 rounded-lg flex-none">{t(language, 'topics_filter_open')}</span>
      </button>

      {/* --- FILTER BOTTOM SHEET --- */}
      {isRendered && (
        <>
          <div 
            className={`fixed inset-0 bg-slate-900/40 z-[60] backdrop-blur-sm md:hidden transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeFilter}
            style={{ touchAction: 'none' }}
          />

          <div 
            className={`
              mobile-sheet-transition ${touchStartY !== null ? 'mobile-sheet-dragging' : ''}
              fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl p-6 pb-28 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] max-h-[85vh] overflow-y-auto box-border
              md:static md:z-auto md:bg-white md:rounded-3xl md:p-8 md:pb-8 md:shadow-sm md:border md:border-slate-200 md:mb-6 md:transform-none
            `} 
            style={{ 
              maxWidth: '100vw', 
              '--sheet-translate': !isVisible ? '100%' : `${dragY}px` 
            }}
          >
            
            <div 
              onTouchStart={handleTouchStart} 
              onTouchMove={handleTouchMove} 
              onTouchEnd={handleTouchEnd}
              className="md:hidden -mt-2 pt-2 pb-6 mb-2 cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
            >
              <div className="w-16 h-2 bg-slate-200 rounded-full mx-auto mb-6 flex-none" />
              
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-slate-800 pointer-events-none">{t(language, 'topics_filter_title')}</h3>
                <button onClick={closeFilter} className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center font-bold text-slate-500 flex-none relative z-10">✕</button>
              </div>
            </div>

            <div className="space-y-8 w-full">
              {/* Författare */}
              <div className="relative w-full">
                <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">{t(language, 'topics_author_label')}</label>
                {!selectedAuthor ? (
                  <div className="flex gap-2 w-full box-border">
                    <input type="text" value={authorQuery} onChange={e => setAuthorQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchForAuthor()} placeholder={t(language, 'topics_author_placeholder')} className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none w-full min-w-0" />
                    <button onClick={searchForAuthor} disabled={isSearchingAuthors} className="bg-slate-200 text-slate-700 px-6 rounded-xl font-bold hover:bg-slate-300 transition-colors active:scale-95 flex-none">
                      {isSearchingAuthors ? '⏳' : t(language, 'topics_search_btn')}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 p-4 rounded-xl w-full box-border">
                    <div className="flex items-center truncate mr-2"><span className="text-green-600 mr-2 flex-none">✅</span><span className="font-bold text-green-900 truncate">{selectedAuthor.name}</span></div>
                    <button onClick={() => setSelectedAuthor(null)} className="text-green-700 hover:text-green-900 font-bold text-sm bg-green-200/50 px-3 py-1 rounded-lg flex-none">{t(language, 'topics_change_btn')}</button>
                  </div>
                )}
                {authorSuggestions.length > 0 && (
                  <ul className="mt-2 border border-slate-200 rounded-xl max-h-40 overflow-y-auto bg-white shadow-lg absolute z-20 left-0 right-0 w-full box-border">
                    {authorSuggestions.map(author => (
                      <li key={author.id} onClick={() => selectAuthor(author)} className="p-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 truncate">
                        <div className="font-bold text-slate-800 truncate">{author.name}</div>
                        <div className="text-xs text-slate-500 truncate">{author.hint}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Tidskrift */}
              <div className="relative w-full">
                <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">{t(language, 'topics_journal_label')}</label>
                {!selectedJournal ? (
                  <div className="flex gap-2 w-full box-border">
                    <input type="text" value={journalQuery} onChange={e => setJournalQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchForJournal()} placeholder={t(language, 'topics_journal_placeholder')} className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none w-full min-w-0" />
                    <button onClick={searchForJournal} disabled={isSearchingJournals} className="bg-slate-200 text-slate-700 px-6 rounded-xl font-bold hover:bg-slate-300 transition-colors active:scale-95 flex-none">
                      {isSearchingJournals ? '⏳' : t(language, 'topics_search_btn')}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-purple-50 border border-purple-200 p-4 rounded-xl w-full box-border">
                    <div className="flex items-center truncate mr-2"><span className="text-purple-600 mr-2 flex-none">✅</span><span className="font-bold text-purple-900 truncate">{selectedJournal.name}</span></div>
                    <button onClick={() => setSelectedJournal(null)} className="text-purple-700 hover:text-purple-900 font-bold text-sm bg-purple-200/50 px-3 py-1 rounded-lg flex-none">{t(language, 'topics_change_btn')}</button>
                  </div>
                )}
                {journalSuggestions.length > 0 && (
                  <ul className="mt-2 border border-slate-200 rounded-xl max-h-40 overflow-y-auto bg-white shadow-lg absolute z-20 left-0 right-0 w-full box-border">
                    {journalSuggestions.map(journal => (
                      <li key={journal.id} onClick={() => selectJournal(journal)} className="p-3 hover:bg-purple-50 cursor-pointer border-b border-slate-100 last:border-0 truncate">
                        <div className="font-bold text-slate-800 truncate">{journal.name}</div>
                        <div className="text-xs text-slate-500 truncate">{journal.hint}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* LÄROSÄTE / SJUKHUS / ORGANISATION */}
              <div className="relative w-full">
                <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">
                  {t(language, 'topics_institution_label')}
                </label>
                {!selectedInstitution ? (
                  <div className="flex gap-2 w-full box-border">
                    <input 
                      type="text" 
                      value={institutionQuery || ''} 
                      onChange={e => setInstitutionQuery && setInstitutionQuery(e.target.value)} 
                      onKeyDown={e => e.key === 'Enter' && searchForInstitution && searchForInstitution()} 
                      placeholder={t(language, 'topics_institution_placeholder')} 
                      className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none w-full min-w-0" 
                    />
                    <button 
                      onClick={searchForInstitution} 
                      disabled={isSearchingInstitutions} 
                      className="bg-slate-200 text-slate-700 px-6 rounded-xl font-bold hover:bg-slate-300 transition-colors active:scale-95 flex-none"
                    >
                      {isSearchingInstitutions ? '⏳' : t(language, 'topics_search_btn')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-amber-50 border border-amber-200 p-4 rounded-xl w-full box-border">
                      <div className="flex items-center truncate mr-2">
                        <span className="text-amber-600 mr-2 flex-none">🏛️</span>
                        <span className="font-bold text-amber-900 truncate">{selectedInstitution.name}</span>
                      </div>
                      <button 
                        onClick={() => setSelectedInstitution && setSelectedInstitution(null)} 
                        className="text-amber-700 hover:text-amber-900 font-bold text-sm bg-amber-200/50 px-3 py-1 rounded-lg flex-none"
                      >
                        {t(language, 'topics_change_btn')}
                      </button>
                    </div>

                    {/* SPECIFIK INSTITUTION / AVDELNING */}
                    <div className="pl-3 border-l-2 border-amber-300">
                      <label className="block text-xs font-bold text-slate-600 mb-1 uppercase tracking-wide">
                        {t(language, 'topics_dept_label')}
                      </label>
                      <input 
                        type="text" 
                        value={departmentQuery || ''} 
                        onChange={e => setDepartmentQuery && setDepartmentQuery(e.target.value)} 
                        placeholder={t(language, 'topics_dept_placeholder')} 
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-slate-800 text-sm" 
                      />
                      <span className="text-[11px] text-slate-400 mt-1 block">
                        {t(language, 'topics_dept_hint')}
                      </span>
                    </div>
                  </div>
                )}
                {institutionSuggestions && institutionSuggestions.length > 0 && (
                  <ul className="mt-2 border border-slate-200 rounded-xl max-h-52 overflow-y-auto bg-white shadow-lg absolute z-20 left-0 right-0 w-full box-border">
                    {institutionSuggestions.map(inst => (
                      <li 
                        key={inst.id} 
                        onClick={() => selectInstitution && selectInstitution(inst)} 
                        className="p-3 hover:bg-amber-50 cursor-pointer border-b border-slate-100 last:border-0 truncate"
                      >
                        <div className="font-bold text-slate-800 truncate">{inst.name}</div>
                        <div className="text-xs text-slate-500 truncate">{inst.hint}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Grupperat Landsfilter / Geografi */}
              <div className="relative w-full">
                <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">{t(language, 'topics_country_label')}</label>
                <div className="relative w-full">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">🌍</span>
                  <select
                    value="" 
                    onChange={(e) => {
                      const code = e.target.value;
                      if (code && !selectedCountries.includes(code)) {
                        setSelectedCountries([...selectedCountries, code]);
                      }
                    }}
                    className="w-full pl-12 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 text-sm md:text-base appearance-none cursor-pointer box-border"
                  >
                    <option value="">{t(language, 'topics_country_placeholder') || 'Välj land för att lägga till...'}</option>
                    <optgroup label={t(language, 'topics_region_nordic') || 'Norden'}>
                      <option value="SE">{t(language, 'country_option_se')}</option>
                      <option value="NO">{t(language, 'country_option_no')}</option>
                      <option value="DK">{t(language, 'country_option_dk')}</option>
                      <option value="FI">{t(language, 'country_option_fi')}</option>
                    </optgroup>
                    <optgroup label={t(language, 'topics_region_weurope') || 'Västeuropa'}>
                      <option value="GB">{t(language, 'country_option_gb')}</option>
                      <option value="DE">{t(language, 'country_option_de')}</option>
                      <option value="FR">{t(language, 'country_option_fr')}</option>
                      <option value="NL">{t(language, 'country_option_nl')}</option>
                      <option value="BE">{t(language, 'country_option_be')}</option>
                      <option value="CH">{t(language, 'country_option_ch')}</option>
                      <option value="AT">{t(language, 'country_option_at')}</option>
                      <option value="IE">{t(language, 'country_option_ie')}</option>
                    </optgroup>
                    <optgroup label={t(language, 'topics_region_seurope') || 'Sydeuropa'}>
                      <option value="ES">{t(language, 'country_option_es')}</option>
                      <option value="IT">{t(language, 'country_option_it')}</option>
                      <option value="PT">{t(language, 'country_option_pt')}</option>
                    </optgroup>
                    <optgroup label={t(language, 'topics_region_namerica') || 'Nordamerika'}>
                      <option value="US">{t(language, 'country_option_us')}</option>
                      <option value="CA">{t(language, 'country_option_ca')}</option>
                    </optgroup>
                    <optgroup label={t(language, 'topics_region_asia') || 'Asien'}>
                      <option value="IN">{t(language, 'country_option_in')}</option>
                      <option value="CN">{t(language, 'country_option_cn')}</option>
                      <option value="JP">{t(language, 'country_option_jp')}</option>
                    </optgroup>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    ▼
                  </div>
                </div>

                {selectedCountries.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 w-full">
                    {selectedCountries.map(code => (
                      <span key={code} className="px-3 py-1.5 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-sm font-semibold flex items-center shadow-sm">
                        {countryNames[code] || code}
                        <button 
                          onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} 
                          className="ml-2 w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center hover:bg-blue-300 transition-colors flex-none text-blue-900"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Datumavgränsning */}
              <div className="pt-2 border-t border-slate-100 w-full">
                <label className="block text-sm font-bold text-slate-700 mb-3 uppercase">{t(language, 'topics_date_label')}</label>
                <div className="flex gap-4 w-full box-border">
                  <div className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-slate-500 mb-1 ml-1">{t(language, 'topics_date_from')}</span>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 text-sm md:text-base box-border" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-slate-500 mb-1 ml-1">{t(language, 'topics_date_to')}</span>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 text-sm md:text-base box-border" />
                  </div>
                </div>
              </div>

              <button 
                onClick={closeFilter}
                className="w-full bg-slate-900 text-white py-4 rounded-2xl font-bold text-lg md:hidden active:scale-95 shadow-lg box-border mt-6"
              >
                {t(language, 'topics_apply_filter')}
              </button>

            </div>
          </div>
        </>
      )}

      {/* --- ACTION KNAPPAR --- */}
      <div className="fixed bottom-[68px] left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-slate-200 md:static md:bg-transparent md:border-none md:p-0 md:backdrop-blur-none z-30 box-border" style={{ maxWidth: '100vw' }}>
        <div className="max-w-3xl mx-auto flex gap-3 md:gap-4 w-full box-border">
          
          {isTrialPaused ? (
            /* När kvoten är slut: Återaktivera-knapp */
            <button 
              onClick={onOpenPaywall}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl py-3.5 md:py-4 font-bold text-base md:text-lg shadow-md transition-all flex items-center justify-center active:scale-95 px-4 gap-2"
            >
              <Lock size={20} className="shrink-0" />
              <span>{pausedBtnText}</span>
            </button>
          ) : (
            /* Normalt läge */
            <>
              <button 
                onClick={performGeneration}
                className="flex-1 bg-white text-slate-700 border-2 border-slate-200 rounded-2xl py-3.5 md:py-4 font-bold text-sm md:text-lg hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center active:scale-95 px-2"
              >
                <span className="mr-1.5 md:mr-2 text-lg md:text-xl flex-none">🔍</span> {t(language, 'topics_test_btn')}
              </button>

              <button 
                onClick={saveWatch}
                className="flex-[2] bg-blue-600 text-white rounded-2xl py-3.5 md:py-4 font-bold text-base md:text-lg shadow-[0_4px_14px_rgba(37,99,235,0.39)] hover:bg-blue-700 transition-all flex items-center justify-center active:scale-95 px-2"
              >
                <span className="mr-1.5 md:mr-2 text-lg md:text-xl flex-none">🛡️</span> {t(language, 'topics_save_btn')}
              </button>
            </>
          )}

        </div>
      </div>

    </div>
  );
};

export default TopicsView;
